import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {NodeApiService} from '../network/online/node/node-api.service';
import {PeerConnectionApiService} from '../peer-connection/peer-connection-api.service';
import {ConnectionApiService} from '../network/online/connection/connection-api.service';
import {RoutingServersSettingsApiService} from '../routing-servers/routing-servers-settings-api.service';
import {RoutingGroupApiService} from '../routing-group/routing-group-api.service';
import {CallsQuotaApiService} from '../settings/routing/calls-quota/calls-quota-api.service';
import {CustomersApiService} from '../customers/customers-api.service';
import {VoipPeerApiService} from '../network/online/voip-peer/voip-peer-api.service';
import {ResourceGroupsApiService} from '../network/online/resource-group/resource-groups-api.service';
import {UrlQueryService} from '../../common/url-query/url-query.service';
import {ReportFilterType} from '../../../statistics/enums/report-filter-type.enum';
import {DateType} from '../../../statistics/enums/date-type.enum';
import {ChartParentType} from '../../../statistics/enums/chart-parent-type.enum';
import * as dayjs from 'dayjs';
import {Observable} from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class StatisticsApiService {

  constructor(private http: HttpClient,
              private nodeService: NodeApiService,
              private pConService: PeerConnectionApiService,
              private connectionService: ConnectionApiService,
              private routerService: RoutingServersSettingsApiService,
              private routingGroupService: RoutingGroupApiService,
              private callsQuotaApiService: CallsQuotaApiService,
              private resourceGroupsApiService: ResourceGroupsApiService,
              private customerApiService: CustomersApiService,
              private voipPeerApiService: VoipPeerApiService) {
  }

  readonly statisticsUrl = 'statistics';
  readonly reportsUrl: string = `${this.statisticsUrl}/report`;
  readonly repositoryUrl: string = `${this.statisticsUrl}/repository`;
  readonly topRoutesUrl = `${this.statisticsUrl}/topRoutes`;

  static extractIdAndName(elements): any {
    return elements.map(node => {
      return {
        id: node.id,
        name: node.name
      };
    });
  }

  static preparePCons(pCons): any {
    return pCons.filter(pCon => pCon.peerConnectionNodeType !== 'Virtual').map(pCon => {
      return {
        id: pCon.id,
        name: `${pCon.name} (${pCon.nodeName})`,
        parentName: pCon.nodeName,
        callsQuotaId: pCon.callsQuotaId
      };
    });
  }

  static prepareNodes(nodes): any {
    let filteredNodes = nodes.filter(node => node.productType !== 'THIRD_PARTY');
    filteredNodes = this.extractIdAndName(filteredNodes);
    return this.sortAsc(filteredNodes);
  }

  static prepareGroups(groups): any {
    return {
      routingGroups: this.sortAsc(StatisticsApiService.extractIdAndName(groups)),
      routingRules: this.sortAsc(this.getRoutingRulesFromGroups(groups))
    };
  }

  static prepareConnection(connections): any {
    connections = this.extractIdAndName(connections);
    return this.sortAsc(connections);
  }

  static prepareRouters(routers): any {
    routers = this.extractIdAndName(routers);
    return this.sortAsc(routers);
  }

  static sortAsc(list): any {
    return list.sort((x, y) => (x.name > y.name) ? 1 : ((x.name < y.name) ? -1 : 0));
  }

  static getRoutingRulesFromGroups(groupList): any {
    const routingRules = [];
    groupList.forEach((group) => {
      group.routingRules.forEach((rule) => {
        const rr = {
          id: rule.id,
          name: `${rule.name} (${group.name})`,
          rrGroupId: group.id,
          rrGroupName: group.name
        };
        routingRules.push(rr);
      });
    });
    return routingRules;
  }

  static buildReportFilter(parameters): any {
    const filter = {};
    const keys = Object.keys(parameters);
    keys.forEach(key => {
      filter[key] = {operator: '=', value: parameters[key]};
    });
    return filter;
  }

  static getInMilliseconds(seconds): any {
    return seconds * 1000;
  }

  static convertUtcToLocalTime(unixTimeInSeconds): any {
    return dayjs(unixTimeInSeconds * 1000);
  }

  static convertToUtcInSeconds(date): any {
    return dayjs.utc(date).valueOf() / 1000;
  }


  public getRepository(): Promise<any> {
    return this.http.get(this.repositoryUrl).toPromise();
  }

  public getReport(params): Promise<any> {
    const uri = `${this.reportsUrl}/${params.reportIdentifier}`;
    const url = UrlQueryService.addQueryParameters(uri, {filter: StatisticsApiService.buildReportFilter(params)});
    return this.http.get(url).toPromise();
  }

  public getReportForChart(params): Promise<any> {
    return this.getReport(this.constructFilterParameters(params)).then(reports => this.constructResponseData(reports));
  }

  public getTopRoutes(params): Observable<any> {
    const url = UrlQueryService.addQueryParameters(this.topRoutesUrl, {filter: params});
    return this.http.get(url);
  }

  public getExternalData(): Promise<any> {
    return Promise.all([
        this.nodeService.getNodes().then(nodes => StatisticsApiService.prepareNodes(nodes)),
        this.pConService.getPCons().then((pCons: any) => StatisticsApiService.preparePCons(pCons.elements)),
        this.connectionService.getConnections().then(connections => StatisticsApiService.prepareConnection(connections.elements)),
        this.routerService.getRoutingServers().toPromise().then(routers => StatisticsApiService.prepareRouters(routers)),
        this.routingGroupService.getGroupsSlim().then(groups => StatisticsApiService.prepareGroups(groups)),
        this.callsQuotaApiService.getCallsQuotas().toPromise(),
        this.resourceGroupsApiService.getGroups(),
        this.customerApiService.getCustomers().toPromise(),
        this.voipPeerApiService.getVoipPeers().then(vps => StatisticsApiService.extractIdAndName(vps.elements))
      ]
    );
  }

  private constructFilterParameters(parameters): any {
    const result: any = {
      reportIdentifier: parameters.reportIdentifier
    };

    parameters.availableFilters.forEach((filter) => {
      switch (filter) {
        case ReportFilterType.DATE:
          result.dateType = parameters.dateType;
          if (result.dateType === DateType.RELATIVE) {
            result.relativeTimeUnit = parameters.relativeTimeUnit;
            result.relativeTimeUnitValue = parameters.relativeTimeUnitValue;
          } else {
            result.startDate = StatisticsApiService.convertToUtcInSeconds(parameters.date.startDate);
            result.endDate = StatisticsApiService.convertToUtcInSeconds(parameters.date.endDate);
          }
          break;
        case ReportFilterType.ELEMENTS:
          result.elements = parameters.elements;
          break;
        case ReportFilterType.SINGLE_ELEMENT:
          result.element = parameters.element;
          break;
        case ReportFilterType.NUMBER_OF_ELEMENTS:
          result.numberOfElements = parameters.numberOfElements;
          break;
        case ReportFilterType.NUMBER_OF_STACKED_ELEMENTS:
          result.numberOfStackedElements = parameters.numberOfStackedElements;
          break;
        case ReportFilterType.SINGLE_STATISTIC:
          result.statistics = [parameters.singleStatistic];
          break;
        case ReportFilterType.MULTIPLE_STATISTICS:
          result.statistics = parameters.multipleStatistics;
          break;
        case ReportFilterType.OVER_TIME_GRAPH_TYPE:
          result.overTimeGraphType = parameters.overTimeGraphType;
          break;
        case ReportFilterType.CALLS_QUOTA_ELEMENTS:
          result.quotaElementType = parameters.quotaElementType;
          result.quotaElementId = parameters.quotaElementId;
          break;
      }
    });
    return result;
  }

  private constructResponseData(value): any {
    return {
      parentType: value.graphType,
      title: value.title,
      subTitle: value.subTitle,
      startDate: value.data.startDate ? StatisticsApiService.convertUtcToLocalTime(value.data.startDate) : undefined,
      interval: value.data.interval ? StatisticsApiService.getInMilliseconds(value.data.interval) : undefined,
      series: this.buildSeries(value.data.series, value.graphType),
      limit: value.data.limit
    };
  }

  private buildSeries(series, graphType): any {
    if (graphType === ChartParentType.COLUMN) {
      return this.buildSeriesForColumnChart(series);
    } else if (graphType === ChartParentType.LINE) {
      return this.buildSeriesForLineChart(series);
    } else {
      return this.buildSeriesForStackedColumnChart(series);
    }
  }

  private buildSeriesForColumnChart(series): any {
    const result = [];
    series.forEach((s) => {
      const item = {
        name: s.statisticName ? s.statisticName : s.elementName,
        data: s.data
      };
      result.push(item);
    });
    return result;
  }

  private buildSeriesForLineChart(series): any {
    const result = [];
    series.forEach((s) => {
      const name = s.statisticName ? s.statisticName : s.elementName;
      const data = [];
      s.data.forEach((element) => {
        data.push({
          y: element.y,
          additionalInfo: element.additionalInfo
        });
      });
      const stat: any = {name, data};
      if (s.label) {
        stat.label = {enabled: true, onArea: false, style: {fontWeight: 100, 'font-size': 10}};
        stat.enableMouseTracking = false;
      } else {
        stat.label = {enabled: false};
      }
      if (s.color) {
        stat.color = s.color;
      }

      stat.marker = {enabled: s.marker};

      result.push(stat);
    });
    return result;
  }

  private buildSeriesForStackedColumnChart(series): any {
    const result = [];
    series.forEach((s) => {
      s.data.forEach((element) => {
        const item = {
          name: element.name,
          data: [
            {
              name: s.elementName,
              y: element.y
            }
          ]
        };
        result.push(item);
      });
    });
    return result;
  }
}
